import type { ReactNode } from 'react';
import { IoAlert } from 'react-icons/io5';

import { BsArrowUpSquare, BsCameraVideo, BsCameraVideoOffFill, BsCcSquare, BsMic, BsMicMuteFill } from 'react-icons/bs';
import { FaRegHandPaper, FaUsers } from 'react-icons/fa';
import { MdBarChart, MdCallEnd, MdEditNote } from 'react-icons/md';
import { SlBubbles } from 'react-icons/sl';
import { BiChalkboard } from 'react-icons/bi';
import { GoGear } from 'react-icons/go';
import { TbBackground } from 'react-icons/tb';

import { CalculatorLogo, QuestionAndAnswerIcon } from '@src/assets/icons';
import { EUserType } from '@src/types';

import { formatCohostLabel, toKFormat } from '@src/utils';

import useRoomFooter from './useRoomFooter';

import { useCalculatorStore } from '@src/stores';
import { useCalculatorActions } from '@src/hooks';
import { useRoomContext } from '@src/contexts';
import { useShallow } from 'zustand/react/shallow';

export type TFooterActionId =
  | 'endCall'
  | 'threeDots'
  | 'mic'
  | 'camera'
  | 'reactions'
  | 'raiseHand'
  | 'shareScreen'
  | 'chat'
  | 'poll'
  | 'qna'
  | 'cc'
  | 'editor'
  | 'whiteboard'
  | 'virtualBackground'
  | 'attendees'
  | 'settings'
  | 'calculator';

export type TFooterActionPosition = 'web-center' | 'web-right' | 'mobile-top' | 'mobile-expanded';

export type TBadgeConfig = {
  show: boolean;
  content: ReactNode;
  className?: string;
};

export type TCTAConfig = {
  type: 'micRequest' | 'camRequest';
  hostName: string;
  message: string;
  buttonLabel: string;
  roleLabel?: string;
  // Runtime data needed for rendering
  request: any; // TMediaRequest
  anchorRef: React.RefObject<HTMLDivElement>;
  onConfirm: () => void;
  onClose: () => void;
};

export type TCountDisplayConfig = {
  show: boolean;
  value: string | number;
  className?: string;
  activeClassName?: string;
};

export type TLoaderConfig = {
  show: boolean;
  className?: string;
};

export type TMediaExtensionConfig = {
  show: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

export type TFooterActionConfig = {
  id: TFooterActionId;
  /**
   * All layout positions where this action should be rendered.
   * E.g. ['mobile-top', 'web-right'].
   */
  position: TFooterActionPosition[];
  /**
   * Base icon glyph for this action. Layouts can wrap it with containers / badges as needed.
   */
  icon: ReactNode;
  /**
   * Optional text label (rarely used on web, more for mobile or accessibility).
   */
  label?: string;
  /**
   * Tooltip text primarily for web.
   */
  tooltip?: string;
  /**
   * Whether this action should be rendered at all for the current user / room.
   */
  isVisible: boolean;
  /**
   * Whether the control is in an "active" state, e.g. raised hand, sharing screen, etc.
   */
  isActive?: boolean;
  /**
   * Whether the control should be disabled (cannot be clicked).
   */
  isDisabled?: boolean;
  /**
   * Whether to show a generic notification indicator for this action (chat, poll, etc.).
   */
  hasNotification?: boolean;
  /**
   * Core click handler for this action (layout-specific wrappers may add extra behavior,
   * e.g. closing mobile sheets, but should delegate to this for business logic).
   */
  onClick?: () => void;
  /**
   * Optional ref to attach to the button element (e.g. for mic/cam to anchor CTAs).
   */
  ref?: React.RefObject<HTMLDivElement>;
  /**
   * Presentation & layout configuration per surface.
   */
  web?: {
    /**
     * Additional CSS class names to apply to the button container on web.
     * E.g. 'icon-with-text', 'red', etc.
     */
    containerClass?: string;
    /**
     * Visual variant for the button (used to apply standard styles).
     */
    variant?: 'danger' | 'media';
    /**
     * Whether this action should render a custom component instead of standard button.
     * E.g. 'emojis' for the Emojis picker, 'threeDots' for ThreeDotsPopper.
     */
    customRender?: 'emojis' | 'threeDots';
    /**
     * Badge configuration for this action (e.g. mic/cam permission warnings).
     */
    badge?: TBadgeConfig;
    /**
     * CTA/popover configuration (e.g. MediaRequestCta for mic/cam requests).
     */
    cta?: TCTAConfig;
    /**
     * Count display configuration (e.g. attendees count).
     */
    countDisplay?: TCountDisplayConfig;
    /**
     * Loader configuration (e.g. share screen loading state).
     */
    loader?: TLoaderConfig;
    /**
     * Media extension toggle configuration (e.g. mic/cam device selector).
     */
    mediaExtension?: TMediaExtensionConfig;
    /**
     * Whether the tooltip should be capitalized.
     */
    capitalizeTooltip?: boolean;
    /**
     * Whether this action is in a "closed" state (for mic/cam closed styling).
     */
    isClosed?: boolean;
    /**
     * Whether this action should be disabled when there are active limited polls.
     */
    disableOnLimitedPolls?: boolean;
    /**
     * Whether to show a divider after this action.
     */
    showDividerAfter?: boolean;
  };
  mobile?: {
    /**
     * Additional CSS class names for mobile icon container.
     */
    containerClass?: string;
    /**
     * Custom render type for mobile (e.g. 'emojis' picker).
     */
    customRender?: 'emojis';
    /**
     * Badge configuration for this action.
     */
    badge?: TBadgeConfig;
    /**
     * CTA/popover configuration.
     */
    cta?: TCTAConfig;
    /**
     * Whether this action is in a "closed" state (for mic/cam closed styling).
     */
    isClosed?: boolean;
  };
};

export const useFooterActionsConfig = () => {
  const {
    // access / permissions / role
    isHostUser,
    isHostOrCohost,
    userTypeRoomConfig,
    meetingType,
    currentPeer,
    participantsPeers,
    viewersCount,
    questionEnabled,

    // exit / basic controls
    isExitButtonEnabled,
    handleEndCallClick,

    // raise hand
    isRaiseHandActive,
    isRaiseHandButtonEnabled,
    isRaiseHandDisabled,
    onRaiseHandClick,

    // three dots
    isThreeDotsFooterMenuEnabled,

    // share screen
    shareScreenBtnTooltip,
    isShareButtonEnabled,
    isShareScreenActive,
    isShareScreenLocked,
    isShareScreenLoading,
    hasTempShareAccess,
    hasRequestedShare,
    canUserShareScreen,
    onShareScreenClick,
    handleAskShareScreen,
    isCurrentPeerSharing,
    trialLockCountdown,

    // emojis
    isEmojiRoomButtonEnabled,
    handleEmojiClick,

    // transcription
    isTranscriptionButtonEnabled,
    isTranscriptionActive,
    handleOnClosedCaptionsClicked,

    // camera
    isCamClosed,
    isStartVideoButtonEnabled,
    isCamButtonDisabled,
    handleCamClick,
    camButtonRef,
    isVideoAllowed,
    isVideoExtensionOpen,
    isMicExtensionOpen,
    setIsMicExtensionOpen,
    setIsVideoExtensionOpen,
    camRequest,
    setCamRequest,

    // mic
    isMicClosed,
    isStartAudioButtonEnabled,
    isMicButtonDisabled,
    handleMicClick,
    micButtonRef,
    isAudioAllowed,
    micRequest,
    setMicRequest,

    // whiteboard / editor / settings
    whiteboardSettings,
    handleCloseWhiteboard,
    handleOpenWhiteboard,
    isEditorButtonEnabled,
    isNotesActive,
    hasActiveLimitedPolls,
    isVirtualBackgroundOpen,
    isSettingActive,

    // polls / chat / qna / participants
    isPollEnabled,
    isPollsActive,
    hasUnvisitedPolls,
    isPublicChatEnabled,
    isChatActive,
    hasChatNotifications,
    isQuestionAndAnswerEnabled,
    isQuestionAndAnswerActive,
    isParticipantsButtonEnabled,
    isParticipantsListActive,
    isCalculatorActive,
    setSelectedKey,
    // media devices (web footer)
    speakerDevices,
    audioInputDevices,
    selectedAudio,
    selectedSpeaker,
    videoInputDevices,
    selectedCamera,
    handleChangeMediaDevice,
    isViewerMode,
  } = useRoomFooter();

  // Calculator-related hooks
  const { isCalculatorEnabled } = useRoomContext();
  const { sharedCalculatorData, setIsPersonalCalculatorVisible } = useCalculatorStore(
    useShallow((state) => ({
      sharedCalculatorData: state.sharedCalculatorData,
      setIsPersonalCalculatorVisible: state.setIsPersonalCalculatorVisible,
    }))
  );
  const { handleStopShareCalculator } = useCalculatorActions();

  /**
   * Central definition of all footer actions.
   * Each action can participate in multiple layouts via the `position` field.
   */
  const allActions: TFooterActionConfig[] = [
    // End Call (web-left + mobile-top)
    {
      id: 'endCall',
      position: ['web-center', 'mobile-top'],
      icon: <MdCallEnd />,
      tooltip: 'Leave meeting',
      label: 'Leave',
      isVisible: isExitButtonEnabled,
      onClick: handleEndCallClick,
      web: {
        variant: 'danger',
        showDividerAfter: !isThreeDotsFooterMenuEnabled,
      },
      mobile: {
        containerClass: 'red',
      },
    },
    // Three dots (web-left only)
    {
      id: 'threeDots',
      position: ['web-center'],
      icon: null,
      tooltip: 'More options',
      label: 'More',
      isVisible: isThreeDotsFooterMenuEnabled,
      web: {
        customRender: 'threeDots',
        showDividerAfter: true,
      },
    },
    // Raise hand (web-left + mobile-top)
    {
      id: 'raiseHand',
      position: ['web-center', 'mobile-top'],
      icon: <FaRegHandPaper />,
      tooltip: `${isRaiseHandActive ? 'Lower' : 'Raise'} hand`,
      label: isRaiseHandActive ? 'Lower Hand' : 'Raise Hand',
      isVisible: isRaiseHandButtonEnabled,
      isActive: isRaiseHandActive,
      isDisabled: isRaiseHandDisabled,
      onClick: () => onRaiseHandClick(!isRaiseHandActive),
      web: {
        containerClass: 'icon-with-text',
        capitalizeTooltip: true,
      },
    },
    // Share screen (web-left + mobile-expanded)
    {
      id: 'shareScreen',
      position: ['web-center', 'mobile-expanded'],
      icon: <BsArrowUpSquare />,
      tooltip: shareScreenBtnTooltip,
      label: 'Share Screen',
      isVisible: isShareButtonEnabled,
      isActive: isShareScreenActive,
      isDisabled:
        isShareScreenLoading ||
        (!isShareScreenActive && trialLockCountdown !== null && trialLockCountdown > 0) ||
        (!isHostOrCohost && isShareScreenLocked && userTypeRoomConfig.main.startScreenButton) ||
        (hasRequestedShare && !canUserShareScreen),
      onClick: () => {
        // Prevent multiple clicks while loading, or trial lock when not actively sharing
        if (isShareScreenLoading || (!isShareScreenActive && trialLockCountdown !== null && trialLockCountdown > 0)) {
          return;
        }

        if (
          isHostOrCohost ||
          (!isShareScreenLocked && userTypeRoomConfig.main.startScreenButton) ||
          hasTempShareAccess ||
          isCurrentPeerSharing
        ) {
          onShareScreenClick(!isShareScreenActive);
        } else {
          handleAskShareScreen();
        }
      },
      web: {
        loader: {
          show: isShareScreenLoading || (hasRequestedShare && !canUserShareScreen),
          className: 'share-screen-loading',
        },
      },
      mobile: {
        containerClass:
          !isHostOrCohost && isShareScreenLocked && userTypeRoomConfig.main.startScreenButton ? 'disabled-icon' : '',
      },
    },
    // Reactions (web-left + mobile-top)
    {
      id: 'reactions',
      position: ['web-center', 'mobile-top'],
      icon: null,
      tooltip: 'Reactions',
      label: 'Reactions',
      isVisible: isEmojiRoomButtonEnabled,
      web: {
        containerClass: 'relative-container',
        customRender: 'emojis',
      },
      mobile: {
        customRender: 'emojis',
      },
    },
    // Closed captions (web-left + mobile-expanded)
    {
      id: 'cc',
      position: ['web-center', 'mobile-expanded'],
      icon: <BsCcSquare />,
      tooltip: 'CC',
      label: 'CC',
      isVisible: isTranscriptionButtonEnabled,
      isActive: isTranscriptionActive,
      onClick: handleOnClosedCaptionsClicked,
      web: {
        capitalizeTooltip: true,
      },
      mobile: {
        containerClass: 'disabled-icon',
      },
    },
    // Camera (web-left + mobile-top)
    {
      id: 'camera',
      position: ['web-center', 'mobile-top'],
      icon: isCamClosed ? <BsCameraVideoOffFill /> : <BsCameraVideo />,
      tooltip: `${isCamClosed ? 'Open' : 'Close'} camera`,
      label: isCamClosed ? 'Camera Off' : 'Camera On',
      isVisible: isStartVideoButtonEnabled,
      isDisabled: isCamButtonDisabled,
      onClick: () => handleCamClick(),
      ref: camButtonRef,
      web: {
        variant: 'media',
        capitalizeTooltip: true,
        isClosed: isCamClosed,
        mediaExtension: {
          show: !isVideoAllowed,
          isOpen: isVideoExtensionOpen,
          onToggle: () => {
            if (isMicExtensionOpen) setIsMicExtensionOpen(false);
            setIsVideoExtensionOpen(!isVideoExtensionOpen);
          },
        },
        badge: {
          show: !isVideoAllowed,
          content: <IoAlert size={14} />,
          className: 'notification-badge',
        },
        cta: camRequest
          ? {
              type: 'camRequest',
              hostName: camRequest.from_peer_name,
              message: 'is asking you to turn on your camera.',
              buttonLabel: 'Open Camera',
              roleLabel: formatCohostLabel(camRequest.roleLabel),
              request: camRequest,
              anchorRef: camButtonRef,
              onConfirm: () => {
                handleCamClick();
                setCamRequest(null);
              },
              onClose: () => setCamRequest(null),
            }
          : undefined,
      },
      mobile: {
        isClosed: isCamClosed,
        badge: {
          show: !isVideoAllowed,
          content: <IoAlert size={14} />,
          className: 'notification-badge',
        },
        cta: camRequest
          ? {
              type: 'camRequest',
              hostName: camRequest.from_peer_name,
              message: 'is asking you to turn on your camera.',
              buttonLabel: 'Open Camera',
              roleLabel: formatCohostLabel(camRequest.roleLabel),
              request: camRequest,
              anchorRef: camButtonRef,
              onConfirm: () => {
                handleCamClick();
                setCamRequest(null);
              },
              onClose: () => setCamRequest(null),
            }
          : undefined,
      },
    },
    // Mic (web-left + mobile-top)
    {
      id: 'mic',
      position: ['web-center', 'mobile-top'],
      icon: isMicClosed ? <BsMicMuteFill /> : <BsMic />,
      tooltip: `${isMicClosed ? 'Unmute' : 'Mute'}`,
      label: isMicClosed ? 'Unmute' : 'Mute',
      isVisible: isStartAudioButtonEnabled,
      isDisabled: isMicButtonDisabled,
      onClick: () => handleMicClick(),
      ref: micButtonRef,
      web: {
        variant: 'media',
        capitalizeTooltip: true,
        isClosed: isMicClosed,
        mediaExtension: {
          show: !isAudioAllowed,
          isOpen: isMicExtensionOpen,
          onToggle: () => {
            if (isVideoExtensionOpen) setIsVideoExtensionOpen(false);
            setIsMicExtensionOpen(!isMicExtensionOpen);
          },
        },
        badge: {
          show: !isAudioAllowed,
          content: <IoAlert size={14} />,
          className: 'notification-badge',
        },
        cta: micRequest
          ? {
              type: 'micRequest',
              hostName: micRequest.from_peer_name,
              message: 'is asking you to unmute your mic.',
              buttonLabel: 'Unmute',
              roleLabel: formatCohostLabel(micRequest.roleLabel),
              request: micRequest,
              anchorRef: micButtonRef,
              onConfirm: () => {
                handleMicClick();
                setMicRequest(null);
              },
              onClose: () => setMicRequest(null),
            }
          : undefined,
      },
      mobile: {
        isClosed: isMicClosed,
        badge: {
          show: !isAudioAllowed,
          content: <IoAlert size={14} />,
          className: 'notification-badge',
        },
        cta: micRequest
          ? {
              type: 'micRequest',
              hostName: micRequest.from_peer_name,
              message: 'is asking you to unmute your mic.',
              buttonLabel: 'Unmute',
              roleLabel: formatCohostLabel(micRequest.roleLabel),
              request: micRequest,
              anchorRef: micButtonRef,
              onConfirm: () => {
                handleMicClick();
                setMicRequest(null);
              },
              onClose: () => setMicRequest(null),
            }
          : undefined,
      },
    },
    {
      id: 'calculator',
      position: ['mobile-expanded'],
      icon: <CalculatorLogo />,
      tooltip: 'Calculator',
      label: 'Calculator',
      isVisible: isCalculatorEnabled,
      isActive: isCalculatorActive,
      onClick: () => {
        if (sharedCalculatorData.isOpen && currentPeer?.id === sharedCalculatorData.owner?.peer_id) {
          handleStopShareCalculator();
        }
        // Sync calculator visibility with drawer state
        if (!isCalculatorActive) {
          setIsPersonalCalculatorVisible(true);
        }
        setSelectedKey('calculator');
      },
    },
    // Whiteboard (web-right + mobile-expanded)
    {
      id: 'whiteboard',
      position: ['web-right', 'mobile-expanded'],
      icon: <BiChalkboard />,
      tooltip: 'Whiteboard',
      label: 'Whiteboard',
      isVisible: isHostUser || whiteboardSettings.isActive === true,
      isActive: whiteboardSettings.isActive,
      // Only host users can toggle whiteboard
      onClick: () => {
        if (!isHostUser) return;

        if (whiteboardSettings.isActive) {
          handleCloseWhiteboard();
        } else {
          handleOpenWhiteboard();
        }
      },
      mobile: {
        containerClass: 'disabled-icon',
      },
    },
    // Notepad (web-right + mobile-expanded)
    {
      id: 'editor',
      position: ['web-right', 'mobile-expanded'],
      icon: <MdEditNote />,
      tooltip: 'Notepad',
      label: 'Notepad',
      isVisible: isEditorButtonEnabled,
      isActive: isNotesActive,
      isDisabled: !!hasActiveLimitedPolls,
      onClick: () => setSelectedKey('notes'),
      web: {
        disableOnLimitedPolls: true,
      },
    },
    // Polls (web-right + mobile-expanded)
    {
      id: 'poll',
      position: ['web-right', 'mobile-expanded'],
      icon: <MdBarChart />,
      tooltip: 'Polls',
      label: 'Poll',
      isVisible: isPollEnabled,
      isActive: isPollsActive,
      hasNotification: hasUnvisitedPolls,
      onClick: () => setSelectedKey('polls'),
      web: {
        disableOnLimitedPolls: true,
      },
    },
    // Chat (web-right + mobile-expanded)
    {
      id: 'chat',
      position: ['web-right', 'mobile-expanded'],
      icon: <SlBubbles />,
      tooltip: 'Chat',
      label: 'Chat',
      isVisible: isPublicChatEnabled,
      isActive: isChatActive,
      hasNotification: hasChatNotifications,
      onClick: () => setSelectedKey('chat'),
      web: {
        disableOnLimitedPolls: true,
      },
    },
    // Q&A (web-right + mobile-expanded)
    {
      id: 'qna',
      position: ['web-right', 'mobile-expanded'],
      icon: <QuestionAndAnswerIcon className="wide-icon" />,
      tooltip: 'Question and Answer',
      label: 'Q&A',
      isVisible: !!(questionEnabled && isQuestionAndAnswerEnabled),
      isActive: isQuestionAndAnswerActive,
      hasNotification: hasUnvisitedPolls,
      onClick: () => setSelectedKey('question-and-answer'),
      web: {
        disableOnLimitedPolls: true,
      },
    },
    // Attendees (web-right only)
    {
      id: 'attendees',
      position: ['web-right'],
      icon: <FaUsers />,
      tooltip: 'Attendees',
      label: 'Attendees',
      isVisible: isParticipantsButtonEnabled,
      isActive: isParticipantsListActive,
      onClick: () => setSelectedKey('participants-list'),
      web: {
        containerClass: 'icon-with-text',
        disableOnLimitedPolls: true,
        countDisplay: {
          show: true,
          value:
            meetingType !== 'webinar' || currentPeer?.peer_info.user_type === EUserType.ATTENDEE
              ? participantsPeers.length
              : toKFormat(Number(viewersCount) + participantsPeers.length),
          className: 'items-number',
          activeClassName: 'items-number__active',
        },
      },
    },
    // Virtual Background (mobile-expanded only)
    {
      id: 'virtualBackground',
      position: ['mobile-expanded'],
      icon: <TbBackground className="icon-svg" />,
      tooltip: 'Virtual Background',
      label: 'Virtual Background',
      isVisible: meetingType !== 'webinar' || !isViewerMode,
      isActive: isVirtualBackgroundOpen,
      onClick: () => setSelectedKey('virtual-background'),
    },
    // Settings (mobile-expanded only)
    {
      id: 'settings',
      position: ['mobile-expanded'],
      icon: <GoGear />,
      tooltip: 'Settings',
      label: 'Settings',
      isVisible: true,
      isActive: isSettingActive,
      onClick: () => setSelectedKey('settings'),
    },
  ];

  const filterByPosition = (pos: TFooterActionPosition) =>
    allActions.filter((action) => action.position.includes(pos) && action.isVisible);

  // Web Center/Right Actions (in order)
  const webCenterActions = filterByPosition('web-center');
  const webRightBaseActions = filterByPosition('web-right');
  // Whiteboard is only visible to hosts on web
  const webRightActions = webRightBaseActions.filter((action) => action.id !== 'whiteboard' || isHostUser);

  // Mobile Top Actions (in order) - same order for hosts and non-hosts
  // Order: endCall, mic, camera, reactions, raiseHand
  const mobileTopPanelistOrder: TFooterActionId[] = ['endCall', 'mic', 'camera', 'reactions', 'raiseHand'];
  const mobileTopActionsBase = mobileTopPanelistOrder
    .map((id) => allActions.find((action) => action.id === id))
    .filter(
      (action): action is TFooterActionConfig => !!action && action.position.includes('mobile-top') && action.isVisible
    );

  const mobileTopActions = mobileTopActionsBase;

  // Mobile Expanded Actions (in order)
  const mobileExpandedOrder: TFooterActionId[] = [
    'chat',
    'poll',
    'qna',
    'cc',
    'shareScreen',
    'editor',
    'calculator',
    'whiteboard',
    'virtualBackground',
    'settings',
  ];

  const mobileExpandedActions = mobileExpandedOrder
    .map((id) => allActions.find((action) => action.id === id))
    .filter(
      (action): action is TFooterActionConfig =>
        !!action &&
        action.position.includes('mobile-expanded') &&
        action.isVisible &&
        (action.id !== 'whiteboard' || isHostUser)
    );

  return {
    webCenter: webCenterActions,
    webRight: webRightActions,
    mobileTop: mobileTopActions,
    mobileExpanded: mobileExpandedActions,
    // extra data used by Icons and RoomFooterWeb (so they don't have to call useRoomFooter directly)
    handleEmojiClick,
    hasActiveLimitedPolls,
    isMicExtensionOpen,
    setIsMicExtensionOpen,
    isVideoExtensionOpen,
    setIsVideoExtensionOpen,
    speakerDevices,
    audioInputDevices,
    selectedAudio,
    selectedSpeaker,
    videoInputDevices,
    selectedCamera,
    handleChangeMediaDevice,
  };
};

export default useFooterActionsConfig;
